import { Box3, Vector3 } from "three";
import type { ConnectProject, ExtensionMainMenu } from "trimble-connect-workspace-api";
import { crsMismatch, describeCrs } from "./geo/crs";
import { detectLanguage, setLanguage, t, type Lang } from "./i18n";
import { classifyRaster, GeoRaster, destroyDecoderPool } from "./io/geoRaster";
import { bundleFromFiles, bundleFromZip, type MeshBundle } from "./io/meshBundle";
import { parseOffset, type MeshOffset } from "./io/offset";
import { ZipReader, extensionOf } from "./io/zipReader";
import { ApiAuthError, ApiClient } from "./tc/apiClient";
import { CoreApi, isFolder, type TcItem } from "./tc/coreApi";
import { datasetKeyFor, MeasurementsStore } from "./tc/measurementsStore";
import { archiveFromConnectFile, archiveFromUrl, type MeshServerArchive } from "./tc/meshArchive";
import { parseShareLink } from "./tc/shareLink";
import { WorkspaceBridge } from "./tc/workspace";
import { checkbox, clear, el, keyValue, modal, progressBar, section, selectBox, slider, toast } from "./ui/dom";
import { buildMeasureToolbox, type MeasureToolboxHandle } from "./ui/measureToolbox";
import { buildAnnotateSection, buildOrthoSection, buildPrintExportSection } from "./ui/planPanel";
import { ProjectTree } from "./ui/ProjectTree";
import { sourceFromConnectFile, sourceFromFile, sourceFromUrl, type DatasetSource } from "./ui/sources";
import { OrthoStack } from "./plan/OrthoStack";
import { PlanView } from "./plan/PlanView";
import { downloadBlob, exportPlanPdf, type PrintUiState } from "./plan/pdfExport";
import { errorMessage, formatBytes, formatCoord, formatCount, formatMetres } from "./util/format";
import { MeasureTool, type SavedMeasurement } from "./viewer/MeasureTool";
import { MeshLayer, type MeshShading, type TileLocateMode } from "./viewer/MeshLayer";
import { SavedMeasurementsLayer } from "./viewer/SavedMeasurementsLayer";
import { SwisstopoBackground } from "./viewer/SwisstopoBackground";
import { TerrainLayer } from "./viewer/TerrainLayer";
import { Viewer } from "./viewer/Viewer";

/**
 * Commands Connect's navigation menu sends back on a click (see `setupMenu`) — a submenu of
 * two entries that switches the top-level mode (see `App.mode`). Quick in-scene actions (zoom
 * to extent, top-down, toggle terrain) live as direct controls in the viewer itself instead
 * (the View section below), not in this menu.
 */
const MENU_COMMAND_MODE_VIEWER = "geo3d.modeViewer";
const MENU_COMMAND_MODE_PLAN = "geo3d.modePlan";

/** How often the layers are asked to reconsider what should be resident. */
const STREAM_INTERVAL_MS = 200;

const RASTER_EXTENSIONS = ["tif", "tiff", "gtiff", "geotiff"];
const MESH_ARCHIVE_EXTENSIONS = ["zip"];
/** Loose mesh deliveries: the OBJ tiles plus everything they reference. Only reachable via
 *  local files / drag-and-drop — the project browser opens a single click, so it only ever
 *  deals in single, self-contained files (archives and rasters). */
const MESH_PART_EXTENSIONS = ["obj", "mtl", "jpg", "jpeg", "png", "xyz", "txt"];
/** Extensions the project browser can open directly, one click each. */
const BROWSABLE_EXTENSIONS = [...RASTER_EXTENSIONS, ...MESH_ARCHIVE_EXTENSIONS];

interface LoadedRaster {
  raster: GeoRaster;
  source: DatasetSource;
}

/**
 * Wires the Trimble Connect bridge, the data loaders and the viewer together, and owns the
 * side panel. Layers are rebuilt rather than mutated when their inputs change, which keeps
 * the georeferencing (a single scene origin shared by every layer) unambiguous.
 */
export class App {
  private readonly viewer: Viewer;
  private readonly measure: MeasureTool;
  /** Every saved measurement drawn as a static shape, so the site's Ausmass history is
   *  visible without having to pick each one from the list — see `syncMeasurementsUi`. */
  private readonly savedMeasurementsLayer: SavedMeasurementsLayer;
  private readonly workspace = new WorkspaceBridge();
  private readonly core: CoreApi;
  /** Client for the stateful API (`server/`) — measurements DB, mesh conversion cache, and
   *  the `/api/me` gate probe. Every call carries the caller's own Trimble bearer token,
   *  never a share's scoped one — see `tc/apiClient.ts`. */
  private readonly api: ApiClient;
  private readonly measurementsApi: MeasurementsStore;

  /**
   * Which top-level submenu is active — "viewer" is the existing 3D scene (terrain, mesh,
   * measurements), "plan" is the flat multi-orthophoto stack + PDF export added alongside it.
   * The two are otherwise independent: switching modes never touches the other mode's state.
   */
  private mode: "viewer" | "plan" = "viewer";
  /** Independently loaded TIFFs for the plan-export mode — see `plan/OrthoStack.ts`. */
  private readonly planStack = new OrthoStack();
  private readonly planView: PlanView;
  /** Sidebar-editable print settings for the plan mode — plain mutable state the "Print &
   *  export" section writes into directly, same pattern as the 3D viewer's own `ui` object. */
  private readonly planPrint: PrintUiState = { paper: "A4", orientation: "landscape", scaleDenominator: 500, dpi: 300 };
  /** Mirrors `projectTreeRootId`/`projectTreeApi` (see `ensureProjectTree`) so a mode switch
   *  alone forces the tree to rebuild with the other mode's accept-list and click handler. */
  private projectTreeMode: "viewer" | "plan" | null = null;

  private ortho: LoadedRaster | null = null;
  /** Height model draped as the terrain's geometry; absent, the terrain is a flat plate. */
  private dem: LoadedRaster | null = null;
  /**
   * Name of the project folder a DOM+Ortho pair was loaded from (see `openFolderDataset`).
   * Takes over from `ortho`/`dem`'s own labels for measurement tagging and the info panel
   * once both halves of a pair come from one place.
   */
  private terrainLabel: string | null = null;
  private background: SwisstopoBackground | null = null;
  /** Bumped on every `rebuildBackground()` call so a stale in-flight fetch can be dropped. */
  private backgroundGeneration = 0;
  private meshSource: DatasetSource | null = null;
  private meshBundle: MeshBundle | null = null;
  private shareLabel: string | null = null;
  /**
   * API client backing whatever the project browser is showing. Normally the session
   * client; a share link gets its own so the share's scoped token cannot displace the
   * user's session and narrow the project down to just the shared objects.
   */
  private browseApi: CoreApi;
  /** Share links opened this session, shown in the Info panel as provenance. */
  private readonly shareLinks: string[] = [];
  private shareBaseUrl: string | null = null;
  private manualOffset: MeshOffset | null = null;

  private terrain: TerrainLayer | null = null;
  private mesh: MeshLayer | null = null;
  private rootFolderId: string | null = null;
  /** Bumped on every `resolveProject()` call so a stale in-flight resolution can be dropped. */
  private projectGeneration = 0;

  /** Inline Trimble Connect file browser shown in the Data section once connected. */
  private projectTree: ProjectTree | null = null;
  private projectTreeRootId: string | null = null;
  private projectTreeApi: CoreApi | null = null;

  /**
   * Folder holding whatever dataset is currently open — the mesh archive's folder, or the
   * folder a DOM+Ortho pair (or lone raster) was opened from. Combined with the project id
   * (see `datasetKeyFor`) this is the key measurements are scoped to in the DB (see
   * `tc/measurementsStore`) — measurements live against the data they were taken from, not
   * in one project-wide bucket, so opening a different dataset shows a different measurement
   * list. `null` for local drag-drop files and URL parameters, which have no Trimble Connect
   * folder to scope against.
   */
  private dataFolderId: string | null = null;
  /** Every measurement saved against the current dataset (see `dataFolderId`), loaded from
   *  the DB-backed API (`tc/measurementsStore`) whenever it changes. */
  private measurements: SavedMeasurement[] = [];
  /** Id of the saved measurement currently loaded into the tool, if any — a save while this
   *  is set overwrites that measurement in place instead of creating a new one. Cleared by
   *  `startNewMeasurement` whenever the user deliberately begins a fresh shape. */
  private editingMeasurementId: string | null = null;
  /**
   * Label of the model the viewer currently has open — the mesh's, or the ortho's when no
   * mesh is loaded — used to tag new saves and to filter `measurements` down to the ones
   * relevant to what's on screen. `null` means no model is loaded, so there is nothing to
   * save against yet.
   */
  private measurementsModelLabel: string | null = null;
  private measureToolboxHandle: MeasureToolboxHandle | null = null;

  private exaggeration = 1;
  private busy = 0;
  private sunAzimuth = 315;
  private sunElevation = 45;
  private brightness = 1;

  /** Panel state kept outside the DOM so re-rendering the panel does not reset controls. */
  private readonly ui = {
    terrainVisible: true,
    terrainOpacity: 1,
    terrainWireframe: false,
    // The backdrop is a visual sanity check around the real data, not the data itself — a
    // faint default keeps it from competing with the model it surrounds.
    backgroundOpacity: 0.45,
    meshVisible: true,
    meshOpacity: 1,
    meshShading: "unlit" as MeshShading,
    meshLocateMode: "exact" as TileLocateMode,
  };

  /** Set once the language has been applied at least once, so a same-language re-fire is a no-op. */
  private languageApplied = false;
  private currentLang: Lang = "en";

  private refineAbort: AbortController | null = null;

  private readonly streamTimer: number;
  private readonly statsTimer: number;

  private readonly panel: HTMLElement;
  private readonly statusText: HTMLElement;
  private readonly statusMem: HTMLElement;
  private readonly coordsChip: HTMLElement;
  private readonly scaleChip: HTMLElement;
  private readonly appEl: HTMLElement;
  private readonly appTitleEl: HTMLElement;
  private readonly sidebarToggleEl: HTMLElement;
  private readonly sidebarReopenEl: HTMLElement;
  private readonly measureToolboxContainer: HTMLElement;
  private readonly viewModeToggle: HTMLElement;
  private readonly hudEl: HTMLElement;
  private readonly modeSwitchEl: HTMLElement;
  private readonly planCanvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.viewer = new Viewer(canvas);
    this.measure = new MeasureTool(this.viewer);
    this.savedMeasurementsLayer = new SavedMeasurementsLayer(this.viewer);
    this.core = new CoreApi(() => this.workspace.accessToken());
    this.api = new ApiClient(() => this.workspace.accessToken());
    this.measurementsApi = new MeasurementsStore(() => this.workspace.accessToken());
    this.browseApi = this.core;
    this.panel = requireElement("sidebar-body");
    this.statusText = requireElement("status-text");
    this.statusMem = requireElement("status-mem");
    this.coordsChip = requireElement("hud-coords");
    this.scaleChip = requireElement("hud-scale");
    this.appEl = requireElement("app");
    this.appTitleEl = requireElement("app-title");
    this.sidebarToggleEl = requireElement("sidebar-toggle");
    this.sidebarReopenEl = requireElement("sidebar-reopen");
    this.measureToolboxContainer = requireElement("measure-toolbox");
    this.viewModeToggle = requireElement("view-mode");
    this.hudEl = requireElement("hud");
    this.modeSwitchEl = requireElement("mode-switch");
    this.planCanvas = requireElement("plan-viewport") as HTMLCanvasElement;
    this.planView = new PlanView(this.planCanvas, this.planStack);

    // Streaming is driven by its own timer rather than by the render loop: rendering is
    // on demand, so an idle camera would otherwise stop tiles from ever loading.
    this.streamTimer = window.setInterval(() => this.pumpStreaming(), STREAM_INTERVAL_MS);

    this.setupChrome();
    this.setupPointer();
    this.viewer.start();
    this.statsTimer = window.setInterval(() => this.refreshStats(), 700);
  }

  /**
   * Loads whatever the page URL asks for.
   *
   * Supported query parameters, each repeatable:
   *   ?share=<share link or token>   any shared file, routed by its extension
   *   ?ortho=<url>  ?mesh=<url>   direct URLs, forced into a given layer
   *
   * This is what lets the viewer be an entry point in its own right: a single URL opens a
   * site with its imagery and mesh already attached. Parameters on the extension URL
   * registered in the manifest arrive here too.
   */
  private async loadFromUrlParameters(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    const shares = params.getAll("share");
    const orthos = params.getAll("ortho");
    const meshes = params.getAll("mesh");
    if (shares.length === 0 && orthos.length === 0 && meshes.length === 0) return false;

    for (const share of shares) {
      await this.openShareLink(share);
    }
    for (const url of orthos) {
      const source = await this.withBusy(t().statusOpening(url), async () => sourceFromUrl(url));
      if (source) await this.loadRasterFile(source);
    }
    for (const url of meshes) {
      const source = await this.withBusy(t().statusOpening(url), async () => sourceFromUrl(url));
      if (source) await this.openMeshFromSource(source, archiveFromUrl(url));
    }
    return true;
  }

  /** Gives the layers a chance to request or release tiles for the current camera. */
  private pumpStreaming(): void {
    const height = this.viewer.renderer.domElement.height;
    const now = performance.now();
    this.terrain?.update(this.viewer.camera, height, now);
    this.mesh?.update(this.viewer.camera, height, now);
  }

  async start(): Promise<void> {
    this.setStatus(t().statusConnecting);

    // Some hosts (notably the native 3D Viewer panel placement) haven't attached the
    // project to the extension by the time the handshake itself resolves — the initial
    // `project.getProject()` call inside connect() can come back empty, and the real
    // project only shows up afterwards as a `project.onChanged` event. Subscribing before
    // connect() runs means that late arrival (and any later project switch) still resolves
    // the root folder and un-grays "Connect…", instead of the panel being stuck on
    // whatever connect() saw at that first instant.
    this.workspace.events.on("project", (project) => void this.resolveProject(project));
    this.workspace.events.on("command", (command) => this.handleMenuCommand(command));
    this.workspace.events.on("language", (language) => this.applyLanguage(language));

    await this.workspace.connect();
    // Mirrors the projectGeneration fallback below: connect() emits "language" synchronously
    // (before it returns) whenever getSettings() succeeded, so this only does anything when
    // that raced with attaching the listener above, or didn't fire at all (standalone).
    this.applyLanguage(this.workspace.language);

    // This extension is only meant to be used embedded in Trimble Connect. Standalone mode
    // (opened directly) stays available under `npm run dev` (`import.meta.env.DEV`, unset in
    // the production build) purely so it can be developed and tested without an actual
    // Connect host to embed in — a deployed build blocks it outright instead of falling back
    // to the old "local files, URLs and drag-and-drop only" mode.
    if (!this.workspace.connected) {
      if (!import.meta.env.DEV) {
        this.renderStandaloneBlockedGate();
        return;
      }
    } else {
      const ok = await this.checkAccess();
      if (!ok) return; // the gate is shown; its retry button resumes via afterAccessGranted
    }
    await this.afterAccessGranted();
  }

  private renderStandaloneBlockedGate(): void {
    const gate = el("div", { id: "auth-gate" });
    gate.append(el("div", { class: "auth-gate-box" }, el("h1", { text: t().standaloneBlockedTitle }), el("p", { text: t().standaloneBlockedMessage })));
    this.appEl.append(gate);
  }

  /** Everything that used to run unconditionally at the end of `start()`, factored out so
   *  the auth gate's retry button can resume here after a first failed attempt instead of
   *  re-running the whole handshake. */
  private async afterAccessGranted(): Promise<void> {
    if (this.workspace.host === "project") void this.setupMenu();
    // Now that `workspace.connected`/`host` are settled, decide whether the in-app mode
    // switch fallback is needed at all (see `rebuildModeSwitch`).
    this.rebuildModeSwitch();
    this.renderPanel();

    // Deep links are honoured whether or not the handshake succeeded, so the same URL
    // works as an extension panel, as a standalone page and as a shared link.
    const bootstrapped = await this.loadFromUrlParameters();

    if (!this.workspace.connected) {
      if (!bootstrapped) this.setStatus(t().statusStandalone);
      return;
    }

    // connect() emits "project" synchronously (before it returns) whenever its own
    // getProject() call succeeded, so the listener above has already handled that case by
    // now — projectGeneration only stays at 0 here if getProject() failed and no event
    // fired at all, which still needs a status set.
    if (this.projectGeneration === 0) await this.resolveProject(this.workspace.project);
  }

  /**
   * Gates the whole app behind a verified, whitelisted Trimble Connect identity: resolves
   * the caller's own session token and probes `/api/me` (which re-verifies it against
   * Trimble's identity provider and checks the server's whitelist — see `server/src/auth.ts`
   * — rather than trusting anything the client asserts). Shows a full-screen gate while
   * pending and on failure, with a retry button that re-enters here.
   */
  private async checkAccess(): Promise<boolean> {
    this.renderAuthGate(t().authGateSigningIn);
    try {
      await this.workspace.accessToken();
    } catch (error) {
      this.renderAuthGate(t().authGateSignInFailed(errorMessage(error)), { retry: true });
      return false;
    }
    try {
      await this.api.me();
      this.hideAuthGate();
      return true;
    } catch (error) {
      if (error instanceof ApiAuthError && error.status === 403) {
        this.renderAuthGate(t().authGateNotWhitelisted(error.email ?? ""), { retry: true });
      } else if (error instanceof ApiAuthError) {
        this.renderAuthGate(t().authGateSignInFailed(error.message), { retry: true });
      } else {
        this.renderAuthGate(t().authGateError(errorMessage(error)), { retry: true });
      }
      return false;
    }
  }

  private renderAuthGate(message: string, options: { retry?: boolean } = {}): void {
    let gate = document.getElementById("auth-gate");
    if (!gate) {
      gate = el("div", { id: "auth-gate" });
      this.appEl.append(gate);
    }
    clear(gate as HTMLElement);
    const box = el("div", { class: "auth-gate-box" }, el("h1", { text: t().authGateTitle }), el("p", { text: message }));
    if (options.retry) {
      const button = el("button", { class: "primary", type: "button", text: t().authGateRetry });
      button.addEventListener("click", () => {
        void (async () => {
          if (await this.checkAccess()) await this.afterAccessGranted();
        })();
      });
      box.append(button);
    }
    gate.append(box);
  }

  private hideAuthGate(): void {
    document.getElementById("auth-gate")?.remove();
  }

  /**
   * Publishes this extension's own entry in Trimble Connect's web navigation menu — only
   * meaningful for the "project" placement (see the manifest's `extensionType`). Its two
   * submenu items are the mode switch (see `App.mode`) — this is the *only* way to switch
   * modes once embedded in Connect; the in-app fallback in `rebuildModeSwitch` only shows up
   * when there is no native menu to use (standalone/dev). A click on either entry comes back
   * as an `extension.command` event, handled by `handleMenuCommand`.
   */
  private async setupMenu(): Promise<void> {
    const icon = new URL("icon.svg", document.baseURI).href;
    const menu: ExtensionMainMenu = {
      title: t().menuTitle,
      icon,
      command: MENU_COMMAND_MODE_VIEWER,
      subMenus: [
        { command: MENU_COMMAND_MODE_VIEWER, title: t().modeViewer },
        { command: MENU_COMMAND_MODE_PLAN, title: t().modePlan },
      ],
    };
    await this.workspace.setMenu(menu);
    await this.workspace.setActiveMenuItem(this.mode === "plan" ? MENU_COMMAND_MODE_PLAN : MENU_COMMAND_MODE_VIEWER);
  }

  private handleMenuCommand(command: string): void {
    if (command === MENU_COMMAND_MODE_VIEWER) this.setMode("viewer");
    else if (command === MENU_COMMAND_MODE_PLAN) this.setMode("plan");
  }

  /**
   * Applies the Trimble Connect user's chosen language to every part of the chrome, live —
   * called once at startup and again whenever `user.onSettingsChanged` fires. `raw` is the
   * unnormalised `UserSettings.language` (e.g. `"de"`, `"fr-FR"`); unsupported/absent values
   * fall back to English.
   */
  private applyLanguage(raw: string | null): void {
    const lang = detectLanguage(raw);
    if (this.languageApplied && lang === this.currentLang) return;
    setLanguage(lang);
    this.currentLang = lang;
    this.languageApplied = true;

    document.documentElement.lang = lang;
    document.title = t().documentTitle;
    this.appTitleEl.textContent = t().appTitle;
    this.appEl.dataset.dropHint = t().dropHint;
    this.sidebarToggleEl.title = t().collapsePanel;
    this.sidebarToggleEl.setAttribute("aria-label", t().collapsePanel);
    this.sidebarReopenEl.title = t().showPanel;
    this.sidebarReopenEl.setAttribute("aria-label", t().showPanel);

    this.rebuildMeasureToolbox();
    this.rebuildViewModeToggle();
    this.rebuildModeSwitch();
    // Forces ensureProjectTree() to rebuild fresh instead of reusing the old instance, whose
    // "Loading…"/"This folder is empty." text was rendered in the previous language.
    this.projectTreeRootId = null;

    if (this.workspace.host === "project" && this.workspace.connected) void this.setupMenu();
    this.renderPanel();
  }

  /**
   * Resolves the Core API region/root folder for whichever project the host currently has
   * open, and re-renders the panel. Called from `start()` for the state connect() already
   * saw, and again from the `project` event for anything that arrives later (see the
   * comment in `start()`).
   */
  private async resolveProject(project: ConnectProject | null): Promise<void> {
    const generation = ++this.projectGeneration;

    if (!project?.id) {
      this.rootFolderId = null;
      this.setStatus(this.workspace.connected ? t().statusNoProject : t().statusStandalone);
      this.renderPanel();
      return;
    }

    try {
      this.setStatus(t().statusResolvingProject(project.name ?? project.id));
      const details = await this.core.resolveRegion(project.id, project.location);
      if (generation !== this.projectGeneration) return; // superseded by a newer project
      this.rootFolderId = details.rootId ?? null;
      this.browseApi = this.core;
      this.setStatus(t().statusConnectedTo(details.name ?? project.id));
      void this.workspace.setStatus(t().statusReady);
      this.renderPanel();
    } catch (error) {
      if (generation !== this.projectGeneration) return;
      this.rootFolderId = null;
      this.setStatus(t().statusFilesUnavailable);
      toast(t().toastCouldNotReachFiles(errorMessage(error)), "warn");
      this.renderPanel();
    }
  }

  // ------------------------------------------------------------------ loading

  private async withBusy<T>(
    label: string,
    fn: (progress: (fraction: number, note?: string) => void) => Promise<T>,
  ): Promise<T | null> {
    this.busy++;
    const release = this.viewer.holdContinuousRender();
    this.setStatus(label);
    try {
      return await fn((fraction, note) => {
        const percent = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
        this.setStatus(`${note ?? label} ${percent}%`);
      });
    } catch (error) {
      toast(t().toastActionFailed(label, errorMessage(error)), "err");
      this.setStatus(t().statusReady);
      return null;
    } finally {
      this.busy--;
      release();
      if (this.busy === 0) this.setStatus(t().statusReady);
      this.renderPanel();
    }
  }

  /**
   * Opens a single GeoTIFF from the project browser, a drag-drop, or a `?ortho=`/`?mesh=`
   * URL parameter. Which half of the terrain it becomes — the photo or the height field —
   * is decided by its own band structure (see `classifyRaster`), not by where it came from,
   * so a lone DOM tif dropped on its own still drapes the terrain with real elevations
   * instead of silently being treated as imagery.
   */
  private async loadRasterFile(source: DatasetSource): Promise<void> {
    await this.withBusy(t().statusReading(source.label), async () => {
      if (!source.range) throw new Error(`${source.label} has no readable stream`);
      const raster = await GeoRaster.open(source.range);

      if (!raster.crs.projected) {
        toast(t().toastGeographicCrs(source.label), "warn");
      }

      if (classifyRaster(raster) === "dem") {
        this.dem?.raster.dispose();
        this.dem = { raster, source };
      } else {
        this.ortho?.raster.dispose();
        this.ortho = { raster, source };
      }
      this.terrainLabel = null; // a single file replaces whatever a folder pair had going
      await this.rebuildTerrain();
      this.refreshMeasurementsModelLabel();
    });
  }

  /**
   * Finds a DOM (height) and an Ortho (photo) GeoTIFF among `items` — a project folder's
   * own listing, already fetched by the browser — and opens both.
   *
   * Filenames are tried first as a cheap short-circuit; when they don't unambiguously say
   * which is which, every raster candidate's header is read and the pair is picked by band
   * structure instead (see `classifyRaster`), which is what actually distinguishes an
   * elevation model from a photo regardless of naming convention.
   */
  private async resolveFolderPair(items: TcItem[]): Promise<{ dom: LoadedRaster; ortho: LoadedRaster } | null> {
    const tifs = items.filter((item) => !isFolder(item) && RASTER_EXTENSIONS.includes(extensionOf(item.name)));
    if (tifs.length < 2) return null;

    const domHint = /(^|[^a-z])(dom|dsm|dtm|mns|mnt|height|elev)([^a-z]|$)/i;
    const orthoHint = /(^|[^a-z])(ortho|photo|rgb|texture)([^a-z]|$)/i;
    const domByName = tifs.find((i) => domHint.test(i.name) && !orthoHint.test(i.name));
    const orthoByName = tifs.find((i) => orthoHint.test(i.name) && !domHint.test(i.name));

    const open = async (item: TcItem): Promise<LoadedRaster> => {
      const source = await sourceFromConnectFile(this.browseApi, item);
      return { raster: await GeoRaster.open(source.range!), source };
    };

    if (domByName && orthoByName && domByName.id !== orthoByName.id) {
      return { dom: await open(domByName), ortho: await open(orthoByName) };
    }

    // Ambiguous by name: open every raster candidate and classify by band structure.
    const opened = await Promise.all(tifs.map(open));
    const dems = opened.filter((o) => classifyRaster(o.raster) === "dem");
    const orthos = opened.filter((o) => classifyRaster(o.raster) === "ortho");
    const winner = dems.length === 1 && orthos.length === 1 ? { dom: dems[0], ortho: orthos[0] } : null;
    for (const o of opened) if (o !== winner?.dom && o !== winner?.ortho) o.raster.dispose();
    return winner;
  }

  /** Loads a DOM+Ortho pair found in a project folder (see the "📦" banner in `ProjectTree`). */
  private async openFolderDataset(folder: TcItem, items: TcItem[]): Promise<void> {
    await this.withBusy(t().statusReading(folder.name), async () => {
      const pair = await this.resolveFolderPair(items);
      if (!pair) {
        toast(t().toastFolderPairAmbiguous(folder.name), "warn");
        return;
      }
      const mismatch = crsMismatch(pair.ortho.raster.crs, pair.dom.raster.crs);
      if (mismatch) toast(t().toastCrsMismatch(folder.name, mismatch), "warn");

      this.ortho?.raster.dispose();
      this.dem?.raster.dispose();
      this.ortho = pair.ortho;
      this.dem = pair.dom;
      this.terrainLabel = folder.name;
      this.dataFolderId = folder.id;
      await this.rebuildTerrain();
      this.refreshMeasurementsModelLabel();
      void this.loadMeasurementsForCurrentFolder();
    });
    this.projectTree?.refresh();
  }

  private async rebuildTerrain(): Promise<void> {
    if (this.terrain) {
      this.viewer.world.remove(this.terrain.group);
      this.viewer.clearHeightOverride();
      this.terrain.dispose();
      this.terrain = null;
      // Rendering is on demand (see Viewer.start): without this, a cleared layer stays
      // drawn on screen — the last rendered frame just isn't repainted — until something
      // else (an orbit, a new tile) happens to request one.
      this.viewer.requestRender();
    }
    if (!this.ortho && !this.dem) {
      await this.rebuildBackground();
      return;
    }

    const layer = new TerrainLayer(this.viewer.origin, this.ortho?.raster ?? null, this.dem?.raster ?? null, {
      fallbackHeight: this.manualOffset?.height ?? this.mesh?.offset?.height ?? 0,
      onChange: () => this.viewer.requestRender(),
    });
    await layer.build((done, total, label) => this.setStatus(`${label} ${done}/${total}`));
    this.terrain = layer;
    layer.setVisible(this.ui.terrainVisible);
    layer.setOpacity(this.ui.terrainOpacity);
    layer.setWireframe(this.ui.terrainWireframe);
    this.viewer.world.add(layer.group);
    // The terrain is drawn as a flat plate, so the cursor readout and the measurement tool
    // need this to see the real DEM elevation under the pointer instead of the flat one.
    this.viewer.setHeightOverride(
      layer.group,
      (east, north) => layer.heightAt(east, north),
      this.viewer.origin.sceneY(layer.groundHeight),
    );
    this.frameAll();
    this.renderPanel();
    await this.rebuildBackground();

    // Real elevations stream in behind the flat imagery, for height queries only — nothing
    // visible changes as they arrive, so the site is usable (and correctly flat) immediately.
    void layer
      .streamHeights((done, total) => {
        if (done % 8 === 0 || done === total) this.setStatus(t().statusReadingElevations(done, total));
      })
      .then(() => this.setStatus(t().statusReady));
  }

  /**
   * (Re)builds the swisstopo aerial-photo backdrop around whatever terrain extent is now
   * loaded, replacing the plain black void outside the model's own footprint with real
   * surroundings — purely a visual sanity check, so a raster in a CRS swisstopo's WMS
   * doesn't accept, or a failed request, just leaves no backdrop rather than surfacing an
   * error. Always clears the previous backdrop first, terrain or no terrain, since the
   * terrain that anchored it may itself have just been cleared.
   */
  private async rebuildBackground(): Promise<void> {
    const generation = ++this.backgroundGeneration;
    if (this.background) {
      this.viewer.world.remove(this.background.group);
      this.background.dispose();
      this.background = null;
      this.viewer.requestRender();
    }
    const raster = this.ortho?.raster ?? this.dem?.raster;
    if (!raster || !this.terrain) return;

    const backdrop = new SwisstopoBackground(this.viewer.origin);
    // Same flat elevation the terrain plate itself is drawn at (see TerrainLayer.groundHeight),
    // so the two never show a visible step between them.
    const built = await backdrop.build(this.terrain.extent, raster.crs.epsg, this.terrain.groundHeight);
    if (generation !== this.backgroundGeneration) {
      backdrop.dispose();
      return;
    }
    if (!built) return;
    this.background = backdrop;
    backdrop.setOpacity(this.ui.backgroundOpacity);
    this.viewer.world.add(backdrop.group);
    this.viewer.requestRender();
  }

  private async loadMesh(source: DatasetSource, bundle: MeshBundle, archive?: MeshServerArchive): Promise<void> {
    await this.withBusy(t().statusIndexing(source.label), async () => {
      if (this.mesh) {
        this.viewer.world.remove(this.mesh.group);
        this.mesh.dispose();
        this.mesh = null;
      }
      this.meshSource = source;
      this.meshBundle = bundle;

      if (bundle.tiles.length === 0) {
        throw new Error(`No .obj tiles found in ${source.label}`);
      }

      const layer = new MeshLayer(this.viewer.origin, bundle, {
        cache: source.cache,
        onChange: () => this.viewer.requestRender(),
        server: archive ? { archive, authToken: () => this.workspace.accessToken() } : undefined,
      });
      await layer.prepare(this.manualOffset);
      this.mesh = layer;
      layer.setVisible(this.ui.meshVisible);
      layer.setShading(this.ui.meshShading);
      this.viewer.world.add(layer.group);

      // `prepare()` only enumerates tiles — it does no I/O, so every tile's extent is still
      // unknown and `bounds()` is empty. Framing right now would hit that empty box and do
      // nothing, leaving the camera wherever it was (often nowhere near the mesh's real-world
      // position) with pan/zoom distances tuned to whatever was framed before. A "fast" locate
      // pass is cheap (metadata-only reads) and is enough to know roughly where the tiles are.
      await layer.locateTiles("fast");
      this.frameAll();

      if (!layer.offsetKnown) {
        toast(t().toastNoOffset(source.label), "warn");
      }

      // Terrain built before the mesh has no elevation reference of its own; adopt the
      // mesh's so a flat ground plane does not end up hundreds of metres below it.
      if (this.terrain && layer.offset) await this.rebuildTerrain();

      this.startTileLocation(layer);
      this.refreshMeasurementsModelLabel();
    });
  }

  /**
   * Locates the mesh tiles in the background. Tiles load regardless of whether their
   * extent is known yet, so this never blocks the viewer.
   */
  private startTileLocation(layer: MeshLayer): void {
    this.refineAbort?.abort();
    const controller = new AbortController();
    this.refineAbort = controller;
    void layer
      .locateTiles(this.ui.meshLocateMode, (done, total) => {
        if (done % 5 === 0 || done === total) this.setStatus(t().statusLocatingTiles(done, total));
        if (done === total) this.renderPanel();
      }, controller.signal)
      .then(() => {
        if (!controller.signal.aborted) this.setStatus(t().statusReady);
      })
      .catch(() => undefined);
  }

  private async openMeshFromSource(source: DatasetSource, archive?: MeshServerArchive): Promise<void> {
    if (!source.range) throw new Error(`${source.label} cannot be read as an archive`);
    const zip = await ZipReader.open(source.range);
    const bundle = await bundleFromZip(zip, source.label);
    await this.loadMesh(source, bundle, archive);
  }

  // --------------------------------------------------------------------- UI

  private setupChrome(): void {
    this.rebuildMeasureToolbox();
    this.rebuildViewModeToggle();
    this.rebuildModeSwitch();

    const sidebar = requireElement("sidebar");
    const toggle = this.sidebarToggleEl;
    const reopen = this.sidebarReopenEl;
    toggle.addEventListener("click", () => {
      sidebar.dataset.open = "false";
      reopen.hidden = false;
    });
    reopen.addEventListener("click", () => {
      sidebar.dataset.open = "true";
      reopen.hidden = true;
    });

    const app = this.appEl;
    let dragDepth = 0;
    app.addEventListener("dragenter", (event) => {
      event.preventDefault();
      dragDepth++;
      app.dataset.dragging = "true";
    });
    app.addEventListener("dragover", (event) => event.preventDefault());
    app.addEventListener("dragleave", () => {
      if (--dragDepth <= 0) {
        dragDepth = 0;
        delete app.dataset.dragging;
      }
    });
    app.addEventListener("drop", (event) => {
      event.preventDefault();
      dragDepth = 0;
      delete app.dataset.dragging;
      const files = event.dataTransfer ? [...event.dataTransfer.files] : [];
      if (files.length) void this.acceptDroppedFiles(files);
    });
  }

  /** (Re)builds the measurement toolbox — needed once at startup and again if the language changes. */
  private rebuildMeasureToolbox(): void {
    clear(this.measureToolboxContainer);
    this.measureToolboxHandle = buildMeasureToolbox(this.measureToolboxContainer, this.measure, {
      listSaved: () => this.measurements.filter((m) => m.modelLabel === this.measurementsModelLabel),
      modelLabel: () => this.measurementsModelLabel,
      saveCurrent: (name) => void this.saveCurrentMeasurement(name),
      loadSaved: (id) => this.loadSavedMeasurement(id),
      renameSaved: (id) => void this.renameSavedMeasurement(id),
      deleteSaved: (id) => void this.deleteSavedMeasurement(id),
      startNew: () => this.startNewMeasurement(),
    });
  }

  /**
   * Builds the 2D/3D toggle docked in the HUD: same scene either way, just whether the
   * camera is locked to a straight-down plan view (pan & zoom only) or free to orbit.
   */
  private rebuildViewModeToggle(): void {
    clear(this.viewModeToggle);
    const button2D = el("button", { type: "button", title: t().view2DTitle }, t().view2D);
    const button3D = el("button", { type: "button", title: t().view3DTitle }, t().view3D);
    const sync = () => {
      button2D.dataset.active = String(this.viewer.isPlanMode);
      button3D.dataset.active = String(!this.viewer.isPlanMode);
    };
    button2D.addEventListener("click", () => {
      this.viewer.setPlanMode(true);
      sync();
    });
    button3D.addEventListener("click", () => {
      this.viewer.setPlanMode(false);
      sync();
    });
    sync();
    this.viewModeToggle.append(button2D, button3D);
  }

  /**
   * The real mode switch lives in Trimble Connect's own navigation submenu (see `setupMenu`)
   * once embedded — that's the only switch a user inside Connect ever sees. This in-app strip
   * is purely a fallback for when there is no such menu to use at all: standalone/dev mode, or
   * a host placement other than "project". It stays hidden the rest of the time.
   */
  private rebuildModeSwitch(): void {
    clear(this.modeSwitchEl);
    const hasNativeMenu = this.workspace.connected && this.workspace.host === "project";
    this.modeSwitchEl.hidden = hasNativeMenu;
    if (hasNativeMenu) return;

    const viewerButton = el("button", { type: "button", title: t().modeViewerTitle }, t().modeViewer);
    const planButton = el("button", { type: "button", title: t().modePlanTitle }, t().modePlan);
    const sync = () => {
      viewerButton.dataset.active = String(this.mode === "viewer");
      planButton.dataset.active = String(this.mode === "plan");
    };
    viewerButton.addEventListener("click", () => this.setMode("viewer"));
    planButton.addEventListener("click", () => this.setMode("plan"));
    sync();
    this.modeSwitchEl.append(viewerButton, planButton);
  }

  /**
   * Switches between the 3D viewer and the orthophoto plan mode. The two keep entirely
   * separate state (`ortho`/`dem`/`mesh` vs `planStack`) — switching away and back leaves
   * whatever was loaded in either mode untouched.
   */
  private setMode(mode: "viewer" | "plan"): void {
    if (this.mode === mode) return;
    this.mode = mode;
    const isPlan = mode === "plan";
    this.viewer.canvas.hidden = isPlan;
    this.planCanvas.hidden = !isPlan;
    this.hudEl.hidden = isPlan;
    this.measureToolboxContainer.hidden = isPlan;
    this.rebuildModeSwitch();
    this.renderPanel();
    if (this.workspace.connected && this.workspace.host === "project") {
      void this.workspace.setActiveMenuItem(isPlan ? MENU_COMMAND_MODE_PLAN : MENU_COMMAND_MODE_VIEWER);
    }
    if (isPlan) {
      // The canvas was `hidden` (zero-sized) until now, so its own ResizeObserver may not
      // have caught up yet — sizing it explicitly avoids a stack composited against a stale
      // (often 0×0) canvas size the very first time this mode is opened.
      this.planView.resize();
      if (!this.planStack.isEmpty) this.planView.refresh();
    }
  }

  /** Routes dropped files to the right loader based on their extensions. */
  private async acceptDroppedFiles(files: File[]): Promise<void> {
    const rasters = files.filter((f) => RASTER_EXTENSIONS.includes(extensionOf(f.name)));

    // Plan mode only ever deals in rasters, each added independently to the stack — no
    // DOM+Ortho pairing, no mesh archives.
    if (this.mode === "plan") {
      for (const file of rasters) await this.addPlanRaster(sourceFromFile(file));
      return;
    }

    const zips = files.filter((f) => MESH_ARCHIVE_EXTENSIONS.includes(extensionOf(f.name)));
    const meshParts = files.filter((f) => MESH_PART_EXTENSIONS.includes(extensionOf(f.name)));

    for (const file of zips) {
      const source = sourceFromFile(file);
      await this.openMeshFromSource(source);
    }

    if (zips.length === 0 && meshParts.some((f) => extensionOf(f.name) === "obj")) {
      const bundle = await bundleFromFiles(meshParts, `${meshParts.length} dropped files`);
      await this.loadMesh(
        { label: bundle.label, kind: "local", detail: "Dropped OBJ set", bytes: bundle.totalObjBytes },
        bundle,
      );
    }

    for (const file of rasters) {
      await this.loadRasterFile(sourceFromFile(file));
    }
  }

  // ------------------------------------------------------------ measurements

  /**
   * Loads every measurement saved against whatever dataset `dataFolderId` now points at, from
   * the DB-backed API (`tc/measurementsStore`). Called whenever a dataset finishes opening.
   */
  private async loadMeasurementsForCurrentFolder(): Promise<void> {
    const datasetKey = datasetKeyFor(this.workspace.project?.id, this.dataFolderId);
    this.measurements = [];
    this.editingMeasurementId = null;
    if (datasetKey) {
      try {
        this.measurements = await this.measurementsApi.list(datasetKey);
      } catch (error) {
        toast(t().toastCouldNotLoadMeasurements(errorMessage(error)), "warn");
        this.measurements = [];
      }
    }
    this.syncMeasurementsUi();
  }

  /**
   * Updates which model new saves are tagged with and shown against — the mesh's, or the
   * ortho's when no mesh is loaded. Purely local bookkeeping; the measurement list itself
   * comes from `loadMeasurementsForCurrentFolder`, keyed off `dataFolderId` rather than this
   * label, so this only changes what's shown as context, not which measurements are shown.
   */
  private refreshMeasurementsModelLabel(): void {
    this.measurementsModelLabel =
      this.meshSource?.label ?? this.terrainLabel ?? this.ortho?.source.label ?? this.dem?.source.label ?? null;
    this.syncMeasurementsUi();
  }

  /** Invalidates "editing an existing saved measurement" — see `editingMeasurementId`. This
   *  is also what brings the measurement that had been loaded for editing back into
   *  `savedMeasurementsLayer`'s static view once the user moves on from it. */
  private startNewMeasurement(): void {
    this.editingMeasurementId = null;
    this.syncMeasurementsUi();
  }

  /** Keeps the toolbox's saved-list panel and the static, always-visible shapes drawn by
   *  `savedMeasurementsLayer` in lockstep with `measurements`/`editingMeasurementId` — every
   *  mutation of either goes through here rather than updating one and forgetting the other. */
  private syncMeasurementsUi(): void {
    this.measureToolboxHandle?.refreshSaved();
    this.savedMeasurementsLayer.set(this.measurements, this.editingMeasurementId);
  }

  private async saveCurrentMeasurement(name: string): Promise<void> {
    const result = this.measure.result();
    const points = this.measure.getRealPoints();
    if (!result || !points || !result.closed) return;
    const datasetKey = datasetKeyFor(this.workspace.project?.id, this.dataFolderId);
    if (!datasetKey) {
      toast(t().toastConnectToSaveMeasurements, "warn");
      return;
    }
    if (!this.measurementsModelLabel) {
      toast(t().toastOpenModelFirstToSave, "warn");
      return;
    }
    const modelLabel = this.measurementsModelLabel;
    // A save while a saved measurement is loaded (see loadSavedMeasurement) overwrites it in
    // place rather than creating a new one alongside it.
    const editing = this.editingMeasurementId
      ? this.measurements.find((m) => m.id === this.editingMeasurementId)
      : undefined;

    const record: SavedMeasurement = {
      id: editing?.id ?? crypto.randomUUID(),
      name,
      modelLabel,
      mode: result.mode,
      points,
      base: result.base,
      length: result.length,
      area: result.area,
      volume: result.volume,
      savedAt: new Date().toISOString(),
    };

    await this.withBusy(t().statusSaving(name), async () => {
      await this.measurementsApi.save(datasetKey, record);
      this.measurements = editing
        ? this.measurements.map((m) => (m.id === record.id ? record : m))
        : [...this.measurements, record];
      this.editingMeasurementId = record.id;
      this.syncMeasurementsUi();
      toast(t().toastSavedWith(name, modelLabel), "ok");
    });
  }

  private loadSavedMeasurement(id: string): void {
    const saved = this.measurements.find((m) => m.id === id);
    if (!saved) return;
    if (!this.viewer.origin.isSet) {
      toast(t().toastOpenDatasetFirst, "warn");
      return;
    }
    this.measure.loadSaved(saved.mode, saved.points, true, saved.base);
    this.editingMeasurementId = saved.id;
    // Loading a measurement into the interactive tool is also "selecting" it: drop it from
    // the static overlay so it isn't drawn twice (once editable, once faded underneath).
    this.syncMeasurementsUi();
  }

  private async renameSavedMeasurement(id: string): Promise<void> {
    const saved = this.measurements.find((m) => m.id === id);
    const datasetKey = datasetKeyFor(this.workspace.project?.id, this.dataFolderId);
    if (!saved || !datasetKey) return;
    const name = window.prompt(t().namePrompt, saved.name);
    if (!name || name === saved.name) return;
    const updated: SavedMeasurement = { ...saved, name, savedAt: new Date().toISOString() };
    await this.withBusy(t().statusSaving(name), async () => {
      await this.measurementsApi.save(datasetKey, updated);
      this.measurements = this.measurements.map((m) => (m.id === id ? updated : m));
      this.syncMeasurementsUi();
    });
  }

  private async deleteSavedMeasurement(id: string): Promise<void> {
    const existed = this.measurements.some((m) => m.id === id);
    this.measurements = this.measurements.filter((m) => m.id !== id);
    if (this.editingMeasurementId === id) this.editingMeasurementId = null;
    this.syncMeasurementsUi();
    if (!existed) return;
    await this.withBusy(t().statusUpdatingMeasurements, async () => {
      await this.measurementsApi.remove(id);
    });
  }

  private setupPointer(): void {
    const canvas = this.viewer.canvas;
    let scheduled = false;
    let lastEvent: PointerEvent | null = null;

    const evaluate = () => {
      scheduled = false;
      if (!lastEvent) return;
      const hit = this.viewer.pick(lastEvent.clientX, lastEvent.clientY);
      if (!hit) {
        this.coordsChip.hidden = true;
        return;
      }
      // Undo the vertical exaggeration before reporting a real-world elevation.
      const local = this.viewer.world.worldToLocal(hit.point.clone());
      const projected = this.viewer.origin.toProjected(local);
      this.coordsChip.hidden = false;
      this.coordsChip.textContent =
        `E ${formatCoord(projected.east)}\n` +
        `N ${formatCoord(projected.north)}\n` +
        `H ${projected.height.toFixed(3)} m`;
    };

    canvas.addEventListener("pointermove", (event) => {
      lastEvent = event;
      if (scheduled) return;
      scheduled = true;
      // Picking traverses every loaded mesh, so it runs at most once per frame.
      requestAnimationFrame(evaluate);
    });
    canvas.addEventListener("pointerleave", () => {
      this.coordsChip.hidden = true;
    });
  }

  private setStatus(message: string): void {
    this.statusText.textContent = message;
    this.statusText.title = message;
  }

  private refreshStats(): void {
    const stats = this.viewer.stats();
    const terrain = this.terrain?.stats();
    const mesh = this.mesh?.stats();
    const bytes = (terrain?.textureBytes ?? 0) + (mesh?.bytes ?? 0);
    this.statusMem.textContent =
      `${formatCount(stats.triangles)} tri · ${formatBytes(bytes)}` +
      (stats.fps ? ` · ${stats.fps.toFixed(0)} fps` : "");

    const distance = this.viewer.camera.position.distanceTo(this.viewer.controls.target);
    this.scaleChip.hidden = !(this.terrain || this.mesh);
    this.scaleChip.textContent = `${t().eyeDistance} ${formatMetres(distance)}`;
  }

  private frameAll(): void {
    const box = new Box3();
    if (this.terrain) box.union(this.terrain.bounds());
    if (this.mesh) box.union(this.mesh.bounds());
    if (!box.isEmpty()) {
      box.min.y *= this.exaggeration;
      box.max.y *= this.exaggeration;
      this.viewer.frameBox(box);
    }
  }

  // ------------------------------------------------------------------ panel

  private renderPanel(): void {
    clear(this.panel);
    if (this.mode === "plan") {
      this.panel.append(this.buildPlanDataSection());
      this.panel.append(
        buildOrthoSection(this.planStack, () => this.onPlanChanged(), (files) => {
          // Sequential on purpose — see the comment in `addPlanRaster` about concurrent loads
          // racing the initial view-fit.
          void (async () => {
            for (const file of files) await this.addPlanRaster(sourceFromFile(file));
          })();
        }),
      );
      this.panel.append(buildPrintExportSection(this.planStack, this.planPrint, () => void this.exportPlanPdf()));
      if (!this.planStack.isEmpty) this.panel.append(buildAnnotateSection(this.planView));
      return;
    }
    this.panel.append(this.buildDataSection());
    if (this.terrain) this.panel.append(this.buildTerrainSection());
    if (this.mesh) this.panel.append(this.buildMeshSection());
    this.panel.append(this.buildViewSection());
    this.panel.append(this.buildInfoSection());
  }

  /** "Data" section for the plan mode: just the project browser (rasters only) plus the
   *  manual-token fallback — none of the ortho/dem/mesh "currently loaded" summary rows the
   *  3D viewer's `buildDataSection` shows, since those slots don't exist in this mode. */
  private buildPlanDataSection(): HTMLElement {
    const handle = section(t().data);
    const connected = this.workspace.connected && this.rootFolderId !== null;
    if (connected) {
      handle.body.append(this.ensureProjectTree());
    } else {
      const manual = el("button", { type: "button" }, t().useAccessToken);
      manual.addEventListener("click", () => this.promptManualToken());
      handle.body.append(
        el("div", {
          class: "hint",
          text: this.workspace.connected ? t().filesUnavailableHint : t().standaloneHint,
        }),
        manual,
      );
    }
    handle.setBadge(this.busy > 0 ? t().loadingBadge : null, "warn");
    return handle.root;
  }

  /** Opens a TIFF picked from the plan mode's project browser into the stack. */
  private async openPlanConnectItem(api: CoreApi, item: TcItem): Promise<void> {
    const source = await sourceFromConnectFile(api, item);
    await this.addPlanRaster(source);
    this.projectTree?.refresh();
  }

  /** Adds one more TIFF to the plan stack — see `plan/OrthoStack.ts`. Rasters are kept fully
   *  independent of each other here (unlike the 3D viewer's DOM+Ortho pairing): each opens
   *  into its own layer, visible and paintable in any combination. */
  private async addPlanRaster(source: DatasetSource): Promise<void> {
    await this.withBusy(t().statusReading(source.label), async () => {
      if (!source.range) throw new Error(`${source.label} has no readable stream`);
      const raster = await GeoRaster.open(source.range);
      if (!raster.crs.projected) toast(t().toastGeographicCrs(source.label), "warn");

      const reference = this.planStack.all[0]?.raster.crs;
      if (reference) {
        const mismatch = crsMismatch(reference, raster.crs);
        if (mismatch) toast(t().toastPlanCrsMismatch(source.label, mismatch), "warn");
      }

      // `PlanView.refresh()` fits to the current combined extent itself the first time it's
      // called (see its `!ready` check) — deciding that here via an `isEmpty` snapshot would
      // race when several rasters load concurrently (e.g. a multi-file picker): whichever
      // one's `GeoRaster.open()` happens to resolve first would fit the view to just its own
      // footprint, and any still-loading sibling would never get a look-in.
      this.planStack.add(raster, source);
      this.onPlanChanged();
    });
  }

  /** Keeps the plan canvas and its sidebar list in lockstep with `planStack` — every mutation
   *  (add/remove/reorder/visibility) goes through here rather than updating one and forgetting
   *  the other. */
  private onPlanChanged(): void {
    this.planView.refresh();
    this.renderPanel();
  }

  /** Renders the current stack + annotations to a scaled PDF page and saves it — see
   *  `plan/pdfExport.ts` for how the scale/paper/DPI choice becomes a real-world extent. */
  private async exportPlanPdf(): Promise<void> {
    if (this.planStack.isEmpty) {
      toast(t().exportNeedsLayer, "warn");
      return;
    }
    await this.withBusy(t().statusExportingPdf, async () => {
      const blob = await exportPlanPdf(this.planStack, this.planView.annotations, {
        ...this.planPrint,
        center: this.planView.center,
      });
      downloadBlob(blob, `plan-1-${this.planPrint.scaleDenominator}.pdf`);
      toast(t().toastExportedPdf, "ok");
    });
  }

  private buildDataSection(): HTMLElement {
    const handle = section(t().data);
    const connected = this.workspace.connected && this.rootFolderId !== null;

    if (this.ortho || this.dem || this.meshSource) {
      handle.body.append(
        el(
          "div",
          { class: "field" },
          el("span", { text: this.terrainLabel ? t().loadedFolder(this.terrainLabel) : t().loaded }),
          ...(this.ortho ? [this.loadedRow("🖼", this.ortho.source, () => this.clearOrtho())] : []),
          ...(this.dem ? [this.loadedRow("⛰", this.dem.source, () => this.clearDem())] : []),
          ...(this.meshSource ? [this.loadedRow("🧱", this.meshSource, () => this.clearMesh())] : []),
        ),
      );
    }

    if (connected) {
      handle.body.append(this.ensureProjectTree());
    } else {
      const manual = el("button", { type: "button" }, t().useAccessToken);
      manual.addEventListener("click", () => this.promptManualToken());
      handle.body.append(
        el("div", {
          class: "hint",
          text: this.workspace.connected ? t().filesUnavailableHint : t().standaloneHint,
        }),
        manual,
      );
    }

    handle.setBadge(this.busy > 0 ? t().loadingBadge : null, "warn");
    return handle.root;
  }

  private loadedRow(icon: string, source: DatasetSource, onClear: () => void): HTMLElement {
    const clearButton = el("button", { class: "icon", type: "button", title: t().remove }, "✕");
    clearButton.addEventListener("click", onClear);
    return el(
      "div",
      { class: "row" },
      el("span", { class: "grow", text: `${icon} ${source.label} · ${formatBytes(source.bytes)}` }),
      clearButton,
    );
  }

  private clearOrtho(): void {
    this.ortho?.raster.dispose();
    this.ortho = null;
    if (!this.dem) this.terrainLabel = null;
    void this.rebuildTerrain().then(() => {
      this.refreshMeasurementsModelLabel();
      this.renderPanel();
    });
  }

  private clearDem(): void {
    this.dem?.raster.dispose();
    this.dem = null;
    if (!this.ortho) this.terrainLabel = null;
    void this.rebuildTerrain().then(() => {
      this.refreshMeasurementsModelLabel();
      this.renderPanel();
    });
  }

  private clearMesh(): void {
    if (this.mesh) {
      this.viewer.world.remove(this.mesh.group);
      this.mesh.dispose();
      this.mesh = null;
      this.viewer.requestRender(); // see the comment in rebuildTerrain — same on-demand pitfall
    }
    this.refineAbort?.abort();
    this.meshSource = null;
    this.meshBundle = null;
    this.refreshMeasurementsModelLabel();
    this.renderPanel();
  }

  /**
   * Gets (creating or repointing as needed) the inline Trimble Connect file browser: one
   * click on a project's root folder shows every file straight away, and clicking a file
   * loads it immediately — no "Connect…" step and no kind to pick first, the extension
   * decides that. Its own state (current folder, cached listing) lives on the ProjectTree
   * instance so re-rendering the panel around it doesn't reset navigation or re-fetch.
   */
  private ensureProjectTree(): HTMLElement {
    const isPlan = this.mode === "plan";
    const isLoaded = (item: TcItem) =>
      isPlan
        ? this.planStack.all.some((layer) => layer.source.id === item.id)
        : item.id === this.ortho?.source.id || item.id === this.dem?.source.id || item.id === this.meshSource?.id;

    if (
      this.projectTree &&
      this.projectTreeRootId === this.rootFolderId &&
      this.projectTreeApi === this.browseApi &&
      this.projectTreeMode === this.mode
    ) {
      this.projectTree.refresh();
    } else {
      this.projectTree = new ProjectTree(this.browseApi, {
        rootId: this.rootFolderId!,
        rootName: this.workspace.project?.name ?? t().project,
        accept: isPlan ? RASTER_EXTENSIONS : BROWSABLE_EXTENSIONS,
        isLoaded,
        onOpen: (item, folderId) =>
          isPlan ? void this.openPlanConnectItem(this.browseApi, item) : void this.openConnectItem(this.browseApi, item, folderId),
        // Folder-pair auto-loading (DOM+Ortho) is a 3D-viewer-only concept — plan mode adds
        // rasters one at a time, on purpose, so overlapping ones can be picked apart.
        ...(isPlan ? {} : { folderDatasetExtensions: RASTER_EXTENSIONS, onOpenFolder: (folder, items) => void this.openFolderDataset(folder, items) }),
      });
      this.projectTreeRootId = this.rootFolderId;
      this.projectTreeApi = this.browseApi;
      this.projectTreeMode = this.mode;
    }
    return this.projectTree.root;
  }

  /**
   * Browses project files with a manually supplied token.
   *
   * Inside Connect the host mints the token and none of this is needed. This covers
   * development against a real project from outside the iframe, and gives a way in if a
   * deployment ends up having to bring its own Trimble Identity token.
   */
  private promptManualToken(): void {
    let settled = false;
    const dialog = modal(t().tokenModalTitle, {
      onClose: () => {
        settled = true;
      },
    });

    const token = el("input", { type: "text", placeholder: "eyJ… (Trimble Identity JWT)" });
    const project = el("input", {
      type: "text",
      placeholder: t().projectId,
      value: this.workspace.project?.id ?? "",
    });
    dialog.body.append(
      el("div", { class: "hint", text: t().tokenModalHint }),
      el("div", { class: "field" }, el("span", { text: t().accessToken }), token),
      el("div", { class: "field" }, el("span", { text: t().projectId }), project),
    );

    const apply = el("button", { class: "primary", type: "button" }, t().connect);
    apply.addEventListener("click", () => {
      if (settled) return;
      const tokenValue = token.value.trim();
      const projectValue = project.value.trim();
      if (!tokenValue || !projectValue) {
        toast(t().toastTokenAndProjectRequired, "warn");
        return;
      }
      settled = true;
      dialog.close();
      this.workspace.useToken(tokenValue);
      void this.withBusy(t().statusResolvingProjectGeneric, async () => {
        const details = await this.core.resolveRegion(projectValue);
        this.rootFolderId = details.rootId ?? null;
        this.measurementsModelLabel = null; // no model loaded yet
        this.browseApi = this.core;
        this.setStatus(t().statusConnectedTo(details.name ?? projectValue));
        toast(t().toastReachedProject(details.name ?? projectValue, this.core.resolvedBaseUrl ?? ""), "ok");
      });
    });
    dialog.footer.append(apply);
  }

  /**
   * Opens a Trimble Connect public share link.
   *
   * Reached only via the `?share=` deep link parameter (see `loadFromUrlParameters`) — there
   * is no UI entry point for it.
   *
   * Needs no sign-in and no registered application: the token in the link is exchanged for
   * a scoped access token, and the shared file streams from its normal pre-signed URL.
   */
  private async openShareLink(input: string): Promise<void> {
    const parsed = parseShareLink(input);
    if (!parsed) {
      toast(t().toastNotShareLink, "warn");
      return;
    }

    await this.withBusy(t().statusOpeningShareLink, async () => {
      // A dedicated client: its scoped token stays confined to the shared objects.
      const shareApi = new CoreApi(async () => {
        throw new Error("This share link no longer grants access");
      });
      const share = await shareApi.openShare(parsed.token, parsed.baseUrl);
      if (!this.shareLinks.includes(input)) this.shareLinks.push(input);
      this.shareLabel = `shared ${share.mode === "PUBLIC" ? "publicly" : share.mode ?? ""}`.trim();
      this.shareBaseUrl = shareApi.resolvedBaseUrl;
      toast(t().toastShareOpened(share.objects.length, share.permission ?? "unknown", shareApi.resolvedBaseUrl ?? ""), "ok");

      // A share can point at files or at a folder; folders become the browsable root.
      const folders = share.objects.filter((object) => object.type?.toUpperCase() === "FOLDER");
      if (folders.length > 0) {
        this.rootFolderId = folders[0].id;
        this.browseApi = shareApi;
        this.renderPanel();
        toast(t().toastShareFolder, "info");
      }

      for (const object of share.objects) {
        if (object.type?.toUpperCase() !== "FILE") continue;
        const item: TcItem = {
          id: object.id,
          name: object.name ?? object.id,
          type: "FILE",
          ...(object.versionId ? { versionId: object.versionId } : {}),
        };
        // A share link doesn't expose the shared file's parent folder, so there is nowhere
        // known to put an Ausmass measurements folder for it — see openConnectItem's
        // `folderId` parameter.
        await this.openConnectItem(shareApi, item, null);
      }
      this.renderPanel();
    });
  }

  /** Routes a Connect file into the right layer by extension — no kind to pick, one click.
   *  `folderId` is the folder it was opened from (`null` when unknown, e.g. a direct share
   *  link), and becomes `dataFolderId` — see the field comment. */
  private async openConnectItem(api: CoreApi, item: TcItem, folderId: string | null): Promise<void> {
    const source = await sourceFromConnectFile(api, item);
    const extension = extensionOf(item.name);
    let opened = true;
    if (MESH_ARCHIVE_EXTENSIONS.includes(extension)) {
      await this.openMeshFromSource(source, archiveFromConnectFile(api, item));
    } else if (RASTER_EXTENSIONS.includes(extension)) {
      await this.loadRasterFile(source);
    } else {
      opened = false;
      toast(t().toastUnsupportedFile(item.name), "warn");
    }
    if (opened) {
      this.dataFolderId = folderId;
      void this.loadMeasurementsForCurrentFolder();
    }
    this.projectTree?.refresh();
  }

  private buildTerrainSection(): HTMLElement {
    const terrain = this.terrain!;
    const handle = section(t().terrain);
    const stats = terrain.stats();
    handle.setBadge(t().tilesBadge(stats.tilesWithGeometry, stats.tiles));

    handle.body.append(
      checkbox(t().visible, this.ui.terrainVisible, (value) => {
        this.ui.terrainVisible = value;
        terrain.setVisible(value);
        this.viewer.requestRender();
      }),
      slider(
        t().opacity,
        { min: 0.1, max: 1, step: 0.05, value: this.ui.terrainOpacity, format: (v) => `${Math.round(v * 100)}%` },
        (value) => {
          this.ui.terrainOpacity = value;
          terrain.setOpacity(value);
          this.viewer.requestRender();
        },
      ),
      checkbox(t().wireframe, this.ui.terrainWireframe, (value) => {
        this.ui.terrainWireframe = value;
        terrain.setWireframe(value);
        this.viewer.requestRender();
      }),
      ...(this.background
        ? [
            slider(
              t().backdropOpacity,
              { min: 0.1, max: 1, step: 0.05, value: this.ui.backgroundOpacity, format: (v) => `${Math.round(v * 100)}%` },
              (value) => {
                this.ui.backgroundOpacity = value;
                this.background?.setOpacity(value);
                this.viewer.requestRender();
              },
            ),
          ]
        : []),
      keyValue([
        [t().imageryGsd, Number.isFinite(terrain.orthoResolution) ? `${(terrain.orthoResolution * 100).toFixed(2)} cm` : "—"],
        [t().modelGsd, Number.isFinite(terrain.demResolution) ? `${(terrain.demResolution * 100).toFixed(1)} cm` : "—"],
        [t().tileSize, `${terrain.tileSize.toFixed(1)} m`],
        [t().textures, `${stats.detailTextures} · ${formatBytes(stats.textureBytes)}`],
      ]),
    );
    return handle.root;
  }

  private buildMeshSection(): HTMLElement {
    const mesh = this.mesh!;
    const handle = section(t().mesh);
    const stats = mesh.stats();
    handle.setBadge(t().tilesBadge(stats.loaded, stats.tiles), stats.failed ? "warn" : undefined);

    const progress = progressBar();
    progress.set(stats.tiles ? stats.loaded / stats.tiles : 0);

    handle.body.append(
      checkbox(t().visible, this.ui.meshVisible, (value) => {
        this.ui.meshVisible = value;
        mesh.setVisible(value);
        this.viewer.requestRender();
      }),
      slider(
        t().opacity,
        { min: 0.1, max: 1, step: 0.05, value: this.ui.meshOpacity, format: (v) => `${Math.round(v * 100)}%` },
        (value) => {
          this.ui.meshOpacity = value;
          mesh.setOpacity(value);
          this.viewer.requestRender();
        },
      ),
      selectBox<MeshShading>(
        t().shading,
        [
          { value: "unlit", label: t().shadingUnlit },
          { value: "lit", label: t().shadingLit },
        ],
        this.ui.meshShading,
        (value) => {
          this.ui.meshShading = value;
          mesh.setShading(value);
          this.viewer.requestRender();
        },
      ),
      progress.root,
      keyValue([
        [t().offset, mesh.offset ? `${formatCoord(mesh.offset.east)}, ${formatCoord(mesh.offset.north)}, ${mesh.offset.height.toFixed(3)}` : "—"],
        [t().offsetSource, mesh.offset?.source ?? "—"],
        [t().triangles, formatCount(stats.triangles)],
        [t().resident, formatBytes(stats.bytes)],
        [t().located, `${stats.located}/${stats.tiles}`],
        [t().estimatedExtents, `${mesh.approximateTiles}`],
      ]),
    );

    handle.body.append(
      selectBox<TileLocateMode>(
        t().tilePlacement,
        [
          { value: "exact", label: t().tilePlacementExact },
          { value: "fast", label: t().tilePlacementFast },
        ],
        this.ui.meshLocateMode,
        (value) => {
          this.ui.meshLocateMode = value;
          this.startTileLocation(mesh);
        },
      ),
      el("div", { class: "hint", text: t().tilePlacementHint }),
    );

    const offsetButton = el("button", { type: "button" }, t().setOffsetManually);
    offsetButton.addEventListener("click", () => this.promptOffset());
    handle.body.append(offsetButton);
    return handle.root;
  }

  private promptOffset(): void {
    const current = this.mesh?.offset;
    const value = window.prompt(
      t().offsetPrompt,
      current ? `${current.east} ${current.north} ${current.height}` : "",
    );
    if (!value) return;
    try {
      this.manualOffset = parseOffset(value, "entered manually");
      if (this.meshSource && this.meshBundle) {
        void this.loadMesh(this.meshSource, this.meshBundle);
      }
    } catch (error) {
      toast(errorMessage(error), "err");
    }
  }

  private buildViewSection(): HTMLElement {
    // Open by default: the quick actions below (zoom / top-down / toggle terrain) used to
    // live in Connect's own navigation submenu — now that it's gone, they need to be
    // reachable without an extra click to expand the section.
    const handle = section(t().view, { collapsed: false });
    handle.body.append(
      slider(t().verticalExaggeration, { min: 1, max: 5, step: 0.1, value: this.exaggeration, format: (v) => `${v.toFixed(1)}×` }, (value) => {
        this.exaggeration = value;
        this.viewer.world.scale.set(1, value, 1);
        this.viewer.world.updateMatrixWorld(true);
        this.viewer.requestRender();
      }),
      slider(t().sunAzimuth, { min: 0, max: 360, step: 5, value: this.sunAzimuth, format: (v) => `${v}°` }, (value) => {
        this.sunAzimuth = value;
        this.viewer.setSun(this.sunAzimuth, this.sunElevation);
      }),
      slider(t().sunElevation, { min: 5, max: 89, step: 1, value: this.sunElevation, format: (v) => `${v}°` }, (value) => {
        this.sunElevation = value;
        this.viewer.setSun(this.sunAzimuth, this.sunElevation);
      }),
      slider(t().brightness, { min: 0.2, max: 2, step: 0.05, value: this.brightness, format: (v) => `${v.toFixed(2)}×` }, (value) => {
        this.brightness = value;
        this.viewer.setLightIntensity(1.7 * value, value);
      }),
    );

    const frame = el("button", { type: "button" }, t().zoomToExtent);
    frame.addEventListener("click", () => this.frameAll());
    const top = el("button", { type: "button" }, t().topDown);
    top.addEventListener("click", () => this.lookDown());
    const terrainToggle = el("button", { type: "button", ...(this.terrain ? {} : { disabled: true }) }, t().toggleTerrain);
    terrainToggle.addEventListener("click", () => this.toggleTerrainVisibility());
    handle.body.append(el("div", { class: "row wrap" }, frame, top, terrainToggle));
    return handle.root;
  }

  private lookDown(): void {
    const target = this.viewer.controls.target.clone();
    const distance = this.viewer.camera.position.distanceTo(target);
    this.viewer.camera.position.copy(target).add(new Vector3(0, distance, 0.001));
    this.viewer.controls.update();
    this.viewer.requestRender();
  }

  private toggleTerrainVisibility(): void {
    this.ui.terrainVisible = !this.ui.terrainVisible;
    this.terrain?.setVisible(this.ui.terrainVisible);
    this.viewer.requestRender();
    this.renderPanel();
  }

  private buildInfoSection(): HTMLElement {
    const handle = section(t().info, { collapsed: true });
    const origin = this.viewer.origin;
    const pairs: Array<[string, string]> = [];

    if (this.workspace.project) {
      pairs.push([t().project, this.workspace.project.name ?? this.workspace.project.id]);
      if (this.workspace.project.crs?.name) pairs.push([t().projectCrs, this.workspace.project.crs.name]);
      pairs.push([t().regionApi, this.core.resolvedBaseUrl ?? "not resolved"]);
    }
    pairs.push([t().host, this.workspace.connected ? this.workspace.host : t().hostStandalone]);
    if (this.shareLinks.length > 0) {
      pairs.push([t().shareLinks, `${this.shareLinks.length}${this.shareLabel ? ` (${this.shareLabel})` : ""}`]);
      if (this.shareBaseUrl) pairs.push([t().shareRegion, this.shareBaseUrl]);
    }
    if (this.ortho) {
      pairs.push([t().orthoCrs, describeCrs(this.ortho.raster.crs)]);
      pairs.push([t().orthoSize, `${formatCount(this.ortho.raster.width)} × ${formatCount(this.ortho.raster.height)} px`]);
      pairs.push([t().orthoLevels, String(this.ortho.raster.levelCount)]);
    }
    if (this.dem) {
      pairs.push([t().domCrs, describeCrs(this.dem.raster.crs)]);
      pairs.push([t().domSize, `${formatCount(this.dem.raster.width)} × ${formatCount(this.dem.raster.height)} px`]);
    }
    pairs.push([t().backdrop, this.background ? t().backdropSwisstopo : t().backdropNone]);
    if (origin.isSet) {
      pairs.push([t().sceneOrigin, `${formatCoord(origin.east)}, ${formatCoord(origin.north)}, ${origin.height.toFixed(0)}`]);
    }
    if (this.terrain) {
      const extent = this.terrain.extent;
      pairs.push([t().extent, `${(extent.maxX - extent.minX).toFixed(1)} × ${(extent.maxY - extent.minY).toFixed(1)} m`]);
      if (Number.isFinite(this.terrain.minHeight)) {
        pairs.push([t().elevation, `${this.terrain.minHeight.toFixed(1)} – ${this.terrain.maxHeight.toFixed(1)} m`]);
      }
    }

    handle.body.append(keyValue(pairs));
    handle.body.append(el("div", { class: "hint", text: t().infoHint }));
    return handle.root;
  }

  dispose(): void {
    window.clearInterval(this.streamTimer);
    window.clearInterval(this.statsTimer);
    this.refineAbort?.abort();
    this.measure.dispose();
    this.savedMeasurementsLayer.dispose();
    this.terrain?.dispose();
    this.mesh?.dispose();
    this.background?.dispose();
    this.ortho?.raster.dispose();
    this.dem?.raster.dispose();
    this.planView.dispose();
    this.planStack.dispose();
    destroyDecoderPool();
    this.viewer.dispose();
  }
}

function requireElement(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing element #${id}`);
  return node;
}
